import type { BottomTabScreenProps } from '@react-navigation/bottom-tabs';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import type {
  CompositeScreenProps,
  LinkingOptions,
  NavigationProp,
  NavigationState,
  NavigatorScreenParams,
  PartialState,
  RouteProp,
} from '@react-navigation/native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import type { AddressType } from '@sweetgreen/garnish';

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace ReactNavigation {
    // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
    interface RootParamList extends AppStackParamList {}
  }
}

export type AppLinkingOptions = LinkingOptions<AppStackParamList>;
export type AppNavigationState = NavigationState<AppStackParamList>;
export type PartialAppNavigationState = PartialState<AppNavigationState>;

export type AppNavigationProp = NavigationProp<AppStackParamList>;
export type AppRouteProp = RouteProp<AppStackParamList>;

// ============================================================
// AppStack
// ============================================================

export const AppStackNavigator =
  createNativeStackNavigator<AppStackParamList>();

export type AppStackParamList = {
  readonly MainTabs: NavigatorScreenParams<MainTabsParamList>;
  readonly Modal: NavigatorScreenParams<ModalStackParamList>;
  readonly Auth: NavigatorScreenParams<AuthStackParamList>;
  readonly Error: NavigatorScreenParams<ErrorStackParamList>;
  readonly Maintenance: NavigatorScreenParams<MaintenanceStackParamList>;
};

export type AppStackChild<
  Keys extends keyof AppStackParamList = keyof AppStackParamList,
> = keyof Pick<AppStackParamList, Keys>;

export type AppStackChildProps<T extends AppStackChild> =
  NativeStackScreenProps<AppStackParamList, T>;

// ============================================================
// AppStack > MainTabs
// ============================================================

export const MainTabsNavigator = createBottomTabNavigator<MainTabsParamList>();

export type MainTabsParamList = {
  readonly HomeTab: NavigatorScreenParams<HomeTabStackParamList>;
  readonly MenuTab: NavigatorScreenParams<MenuTabStackParamList>;
  readonly LoyaltyTab: NavigatorScreenParams<LoyaltyTabStackParamList>;
  readonly AccountTab: NavigatorScreenParams<AccountTabStackParamList>;
};

export type MainTabsScreenProps = AppStackChildProps<'MainTabs'>;

export type MainTabName<
  Keys extends keyof MainTabsParamList = keyof MainTabsParamList,
> = keyof Pick<MainTabsParamList, Keys>;

export type MainTabsChildProps<T extends MainTabName> = CompositeScreenProps<
  BottomTabScreenProps<MainTabsParamList, T>,
  MainTabsScreenProps
>;

// ============================================================
// AppStack > MainTabs > HomeTab
// ============================================================

export const HomeTabStackNavigator =
  createNativeStackNavigator<HomeTabStackParamList>();

export type HomeTabStackParamList = {
  // /
  readonly Home: undefined;
};

export type HomeTabScreenProps = MainTabsChildProps<'HomeTab'>;

export type HomeTabStackScreenName<
  Keys extends keyof HomeTabStackParamList = keyof HomeTabStackParamList,
> = keyof Pick<HomeTabStackParamList, Keys>;

export type HomeTabStackChildProps<T extends HomeTabStackScreenName> =
  CompositeScreenProps<
    NativeStackScreenProps<HomeTabStackParamList, T>,
    HomeTabScreenProps
  >;

export type HomeScreenProps = HomeTabStackChildProps<'Home'>;

// ============================================================
// AppStack > MainTabs > MenuTab
// ============================================================

export const MenuTabStackNavigator =
  createNativeStackNavigator<MenuTabStackParamList>();

export type MenuTabStackParamList = {
  // /locations
  readonly Location:
    | {
        readonly locationSelectionType?: AddressType;
        readonly previousDeliveryId?: string;
      }
    | undefined;
  // /:locationSlug
  readonly Menu: { readonly restaurantSlug: string };
  // /delivery/:addressId
  readonly DeliveryMenu: {
    readonly addressId: string;
  };
  // /:locationSlug/collection/:collectionSlug
  readonly MenuCollection: {
    readonly restaurantSlug: string;
    readonly menuCollectionId: string;
    readonly addressId?: string;
  };
  // /delivery/:addressId/collection/:collectionSlug
  readonly DeliveryMenuCollection: {
    readonly addressId: string;
    readonly menuCollectionId: string;
    readonly restaurantSlug?: string;
  };
};

export type MenuTabScreenProps = MainTabsChildProps<'MenuTab'>;

export type MenuTabStackScreenName<
  Keys extends keyof MenuTabStackParamList = keyof MenuTabStackParamList,
> = keyof Pick<MenuTabStackParamList, Keys>;

export type MenuTabStackChildProps<T extends MenuTabStackScreenName> =
  CompositeScreenProps<
    NativeStackScreenProps<MenuTabStackParamList, T>,
    MenuTabScreenProps
  >;

export type LocationScreenProps = MenuTabStackChildProps<'Location'>;
export type MenuScreenProps = MenuTabStackChildProps<'Menu'>;
export type DeliveryMenuScreenProps = MenuTabStackChildProps<'DeliveryMenu'>;
export type MenuCollectionScreenProps =
  MenuTabStackChildProps<'MenuCollection'>;
export type DeliveryMenuCollectionScreenProps =
  MenuTabStackChildProps<'DeliveryMenuCollection'>;

// ============================================================
// AppStack > MainTabs > LoyaltyTab
// ============================================================

export const LoyaltyTabStackNavigator =
  createNativeStackNavigator<LoyaltyTabStackParamList>();

export type LoyaltyTabStackParamList = {
  // /sweetpass
  readonly Sweetpass: undefined;
  // /challenges
  readonly Challenges: undefined;
};

export type LoyaltyTabScreenProps = MainTabsChildProps<'LoyaltyTab'>;

export type LoyaltyTabStackScreenName<
  Keys extends keyof LoyaltyTabStackParamList = keyof LoyaltyTabStackParamList,
> = keyof Pick<LoyaltyTabStackParamList, Keys>;

export type LoyaltyTabStackChildProps<T extends LoyaltyTabStackScreenName> =
  CompositeScreenProps<
    NativeStackScreenProps<LoyaltyTabStackParamList, T>,
    LoyaltyTabScreenProps
  >;

export type SweetpassScreenProps = LoyaltyTabStackChildProps<'Sweetpass'>;
export type ChallengesScreenProps = LoyaltyTabStackChildProps<'Challenges'>;

// ============================================================
// AppStack > MainTabs > AccountTab
// ============================================================

export const AccountTabStackNavigator =
  createNativeStackNavigator<AccountTabStackParamList>();

export type AccountTabScreenId =
  | 'profile'
  | 'sweetpass-membership'
  | 'payment-credit'
  | 'addresses'
  | 'orders'
  | 'favorites'
  | 'refer-friend'
  | 'dietary-restrictions';

export type AccountTabStackParamList = {
  // /account
  readonly AccountMenu: undefined;

  // /profile
  readonly Profile: undefined;

  // /sweetpass/membership
  readonly SweetpassMembership: undefined;

  // /payment
  readonly PaymentCredit: undefined;

  // /addresses
  readonly Addresses: undefined;

  // /orders
  readonly Orders: undefined;

  // /favorites
  readonly Favorites: undefined;

  // /refer-friend
  readonly ReferFriend: undefined;

  // /account-dietary-restrictions
  readonly AccountDietaryRestrictions: undefined;
};

export type AccountTabScreenProps = MainTabsChildProps<'AccountTab'>;

export type AccountTabStackScreenName<
  Keys extends keyof AccountTabStackParamList = keyof AccountTabStackParamList,
> = keyof Pick<AccountTabStackParamList, Keys>;

export type AccountTabStackChildProps<T extends AccountTabStackScreenName> =
  CompositeScreenProps<
    NativeStackScreenProps<AccountTabStackParamList, T>,
    AccountTabScreenProps
  >;

export type AccountMenuScreenProps = AccountTabStackChildProps<'AccountMenu'>;

export type ProfileScreenProps = AccountTabStackChildProps<'Profile'>;

export type PaymentCreditScreenProps =
  AccountTabStackChildProps<'PaymentCredit'>;

export type SweetpassMembershipScreenProps =
  AccountTabStackChildProps<'SweetpassMembership'>;

export type AddressesScreenProps = AccountTabStackChildProps<'Addresses'>;

export type OrdersScreenProps = AccountTabStackChildProps<'Orders'>;

export type FavoritesScreenProps = AccountTabStackChildProps<'Favorites'>;

export type ReferFriendScreenProps = AccountTabStackChildProps<'ReferFriend'>;

export type AccountDietaryRestrictionsScreenProps =
  AccountTabStackChildProps<'AccountDietaryRestrictions'>;

// ============================================================
// AppStack > Modal
// ============================================================

export const ModalStackNavigator =
  createNativeStackNavigator<ModalStackParamList>();

export type ModalStackParamList = {
  // /:locationSlug/:productSlug
  readonly ProductDetails: {
    readonly restaurantSlug: string;
    readonly productSlug: string;
  };
  // /:locationSlug/:productSlug/edit/:lineItemId
  readonly EditProductDetails: {
    readonly restaurantSlug: string;
    readonly productSlug: string;
    readonly lineItemId: string;
  };

  // /delivery/:addressId/:productSlug
  readonly DeliveryProductDetails: {
    readonly addressId: string;
    readonly productSlug: string;
  };
  // /delivery/:addressId/:productSlug/edit/:lineItemId
  readonly DeliveryEditProductDetails: {
    readonly addressId: string;
    readonly productSlug: string;
    readonly lineItemId: string;
  };

  // /reorder/location/confirm
  readonly ReorderConfirmLocation: undefined;
  // /reorder/locations
  readonly ReorderChangeLocation: undefined;
  // /reorder/conflicts
  readonly ReorderConflictReview: undefined;
  // /reorder/active-bag-warning
  readonly ReorderActiveBagWarning: undefined;

  // /bag
  readonly Bag: undefined;
  // /checkout
  readonly Checkout: undefined;

  // /order
  readonly OrderStatus: {
    readonly orderId: string;
  };

  // /scan-to-pay
  readonly ScanToPay: undefined;

  // /payment/list
  readonly Payments: undefined;
  // /payment/select
  readonly PaymentCredit: undefined;
  // /payment/add
  readonly PaymentMethodAdd: undefined;
  // /payment/:paymentMethodId
  readonly PaymentMethodEdit: { readonly paymentMethodId: string };

  // /credit/add
  readonly CreditAdd: undefined;
  // /credit/scan
  readonly CreditScan: undefined;
  // /credit
  readonly CreditDetails: undefined;

  // /addresses/add
  readonly AddressAdd: undefined;
  // /addresses/:addressId
  readonly AddressEdit: { readonly addressId: string };

  // /dietary-restrictions
  readonly DietaryRestrictions: undefined;

  // /feedback/:orderId
  readonly RateOrder: { readonly orderId: string };

  // /share/:lineItemSlug
  readonly ShareLineItem: { readonly lineItemSlug: string };

  // /ca-data-requests
  readonly PersonalData: undefined;
  readonly PersonalDataDetails: { readonly action: 'access' | 'delete' };

  // delivery in flight modal, no path
  readonly DeliveryOrderInFlight: { readonly orderId: string };

  // /sweetpass/upgrade
  readonly SweetpassUpgrade: undefined;

  // /order-product/:productSlug
  readonly OrderProductConfirmProduct: { readonly productSlug: string };

  // /order-product/:productSlug/confirm-location
  readonly OrderProductConfirmLocation: { readonly productSlug: string };
};

export type ModalStackScreenProps = AppStackChildProps<'Modal'>;

export type ModalStackScreenName<
  Keys extends keyof ModalStackParamList = keyof ModalStackParamList,
> = keyof Pick<ModalStackParamList, Keys>;

export type ModalStackChildProps<T extends ModalStackScreenName> =
  CompositeScreenProps<
    NativeStackScreenProps<ModalStackParamList, T>,
    ModalStackScreenProps
  >;

export type ProductDetailsScreenProps = ModalStackChildProps<'ProductDetails'>;
export type EditProductDetailsScreenProps =
  ModalStackChildProps<'EditProductDetails'>;

export type DeliveryProductDetailsScreenProps =
  ModalStackChildProps<'DeliveryProductDetails'>;
export type DeliveryEditProductDetailsScreenProps =
  ModalStackChildProps<'DeliveryEditProductDetails'>;
export type DeliveryOrderInFlightScreenProps =
  ModalStackChildProps<'DeliveryOrderInFlight'>;

export type ReorderActiveBagWarningScreenProps =
  ModalStackChildProps<'ReorderActiveBagWarning'>;
export type ReorderConfirmLocationScreenProps =
  ModalStackChildProps<'ReorderConfirmLocation'>;
export type ReorderChangeLocationScreenProps =
  ModalStackChildProps<'ReorderChangeLocation'>;
export type ReorderConflictReviewScreenProps =
  ModalStackChildProps<'ReorderConflictReview'>;

export type BagScreenProps = ModalStackChildProps<'Bag'>;
export type CheckoutScreenProps = ModalStackChildProps<'Checkout'>;

export type OrderStatusScreenProps = ModalStackChildProps<'OrderStatus'>;

export type ScanToPayScreenProps = ModalStackChildProps<'ScanToPay'>;

export type PaymentMethodAddScreenProps =
  ModalStackChildProps<'PaymentMethodAdd'>;
export type PaymentMethodEditScreenProps =
  ModalStackChildProps<'PaymentMethodEdit'>;

export type CreditAddScreenProps = ModalStackChildProps<'CreditAdd'>;
export type CreditScanScreenProps = ModalStackChildProps<'CreditScan'>;
export type CreditDetailsScreenProps = ModalStackChildProps<'CreditDetails'>;

export type AddressAddScreenProps = ModalStackChildProps<'AddressAdd'>;
export type AddressEditScreenProps = ModalStackChildProps<'AddressEdit'>;

export type DietaryRestrictionsScreenProps =
  ModalStackChildProps<'DietaryRestrictions'>;

export type RateOrderScreenProps = ModalStackChildProps<'RateOrder'>;
export type ShareLineItemScreenProps = ModalStackChildProps<'ShareLineItem'>;

export type PersonalDataScreenProps = ModalStackChildProps<'PersonalData'>;
export type PersonalDataDetailsScreenProps =
  ModalStackChildProps<'PersonalDataDetails'>;

export type SweetpassUpgradeScreenProps =
  ModalStackChildProps<'SweetpassUpgrade'>;

export type OrderProductConfirmProductScreenProps =
  ModalStackChildProps<'OrderProductConfirmProduct'>;

// ============================================================
// AppStack > Auth
// ============================================================

export type AuthRedirectPath = {
  readonly screen: keyof AppStackParamList;
  readonly params: AppStackParamList[keyof AppStackParamList];
};

export type AuthRedirectTarget =
  | 'home'
  | 'sweetpass'
  | 'challenges'
  | 'locations'
  | 'checkout'
  | AccountTabScreenId;

const AUTH_REDIRECT_PATHS: Record<AuthRedirectTarget, AuthRedirectPath> = {
  home: {
    screen: 'MainTabs',
    params: { screen: 'HomeTab', params: { screen: 'Home' } },
  },
  sweetpass: {
    screen: 'MainTabs',
    params: { screen: 'LoyaltyTab', params: { screen: 'Sweetpass' } },
  },
  challenges: {
    screen: 'MainTabs',
    params: { screen: 'LoyaltyTab', params: { screen: 'Challenges' } },
  },
  locations: {
    screen: 'MainTabs',
    params: {
      screen: 'MenuTab',
      params: { screen: 'Location' },
    },
  },
  checkout: { screen: 'Modal', params: { screen: 'Checkout' } },
  profile: {
    screen: 'MainTabs',
    params: { screen: 'AccountTab', params: { screen: 'Profile' } },
  },
  'sweetpass-membership': {
    screen: 'MainTabs',
    params: { screen: 'AccountTab', params: { screen: 'SweetpassMembership' } },
  },
  'payment-credit': {
    screen: 'MainTabs',
    params: { screen: 'AccountTab', params: { screen: 'PaymentCredit' } },
  },
  addresses: {
    screen: 'MainTabs',
    params: { screen: 'AccountTab', params: { screen: 'Addresses' } },
  },
  orders: {
    screen: 'MainTabs',
    params: { screen: 'AccountTab', params: { screen: 'Orders' } },
  },
  favorites: {
    screen: 'MainTabs',
    params: { screen: 'AccountTab', params: { screen: 'Favorites' } },
  },
  'refer-friend': {
    screen: 'MainTabs',
    params: { screen: 'AccountTab', params: { screen: 'ReferFriend' } },
  },
  'dietary-restrictions': {
    screen: 'MainTabs',
    params: {
      screen: 'AccountTab',
      params: { screen: 'AccountDietaryRestrictions' },
    },
  },
};

export const authRedirectPathFromTarget = (
  target?: AuthRedirectTarget,
): AuthRedirectPath | undefined =>
  target ? AUTH_REDIRECT_PATHS[target] : undefined;

export const AuthStackNavigator =
  createNativeStackNavigator<AuthStackParamList>();

export type AuthStackParamList = {
  // //login
  readonly Login: {
    readonly redirect?: AuthRedirectTarget;
  };
  // /join
  readonly Join: {
    readonly redirect?: AuthRedirectTarget;
  };
  // /register/verify
  readonly JoinVerification: {
    readonly email?: string;
    readonly token?: string;
    readonly redirect?: AuthRedirectTarget;
  };
  // /forgot-password
  readonly ForgotPassword: {
    readonly redirect?: AuthRedirectTarget;
  };
};

export type AuthStackScreenProps = AppStackChildProps<'Auth'>;

export type AuthStackScreenName<
  Keys extends keyof AuthStackParamList = keyof AuthStackParamList,
> = keyof Pick<AuthStackParamList, Keys>;

export type AuthStackChildProps<T extends AuthStackScreenName> =
  CompositeScreenProps<
    NativeStackScreenProps<AuthStackParamList, T>,
    AuthStackScreenProps
  >;

export type LoginScreenProps = AuthStackChildProps<'Login'>;
export type JoinScreenProps = AuthStackChildProps<'Join'>;
export type JoinVerificationScreenProps =
  AuthStackChildProps<'JoinVerification'>;
export type ForgotPasswordScreenProps = AuthStackChildProps<'ForgotPassword'>;

// ============================================================
// AppStack > Error
// ============================================================

export const ErrorStackNavigator =
  createNativeStackNavigator<ErrorStackParamList>();

export type ErrorStackParamList = {
  // /*
  readonly NotFound: undefined;
};

export type ErrorStackScreenProps = AppStackChildProps<'Error'>;

export type ErrorStackScreenName<
  Keys extends keyof ErrorStackParamList = keyof ErrorStackParamList,
> = keyof Pick<ErrorStackParamList, Keys>;

export type ErrorStackChildProps<T extends ErrorStackScreenName> =
  CompositeScreenProps<
    NativeStackScreenProps<ErrorStackParamList, T>,
    ErrorStackScreenProps
  >;

export type NotFoundScreenProps = ErrorStackChildProps<'NotFound'>;

// ============================================================
// AppStack > Maintenance
// ============================================================

export type MaintenanceStackParamList = {
  readonly MaintenanceMode: undefined;
};

export const MaintenanceStackNavigator =
  createNativeStackNavigator<MaintenanceStackParamList>();
